/**
 * OS ÍCONES DA BARRA DE FERRAMENTAS DO EDITOR.
 *
 * `T4.5` do pedido, na letra: *"tem que mudar esses ícones, eles estão muito antigos e muito
 * legais"*. A frase se contradiz — "antigos E muito legais" — e fica como está no ponto. O que ela
 * pede sem ambiguidade é **mudar**, e é isso que este arquivo faz.
 *
 * ── O QUE HAVIA, E POR QUE "ANTIGO" É JUSTO ───────────────────────────────────────────────────
 *
 * Nove PNGs de ~40px em `assets/criar-comic/`, cada um um clipart com fundo de cor própria e
 * arbitrária: o relógio do Histórico num quadrado LARANJA, a personagem num quadrado ROXO, e por aí.
 * Três problemas de uma vez, e nenhum deles é gosto:
 *
 *   1. **Não formam conjunto.** Nove tiles de nove cores sem relação entre si. Cor que não
 *      significa nada é ruído, e numa barra de nove itens é ruído nove vezes.
 *   2. **São raster de baixa resolução.** A barra desenha o ícone a 30% da largura do botão, e num
 *      monitor 2x eles borram. Um ícone borrado é o sinal mais barato de produto inacabado —
 *      exatamente o `T4.9`.
 *   3. **Não obedecem à marca.** O `especificacao/001-estetica-e-marca.md` decide paleta e traço,
 *      e esses PNGs vieram de outro lugar.
 *
 * ── O SISTEMA, E ELE CARREGA INFORMAÇÃO ───────────────────────────────────────────────────────
 *
 * A cor do tile agrupa por FUNÇÃO, e não por item. Quem usa a barra aprende quatro cores em vez de
 * decorar nove:
 *
 *   menta     o que entra na página    Personagens · Cenários · Figuras
 *   amarelo   o que fala               Textos
 *   laranja   o que estrutura a folha  Inserir Quadro · Redimensionar
 *   cinza     memória e saída          Histórico · Exportar para PDF · Gerar imagem
 *
 * O traço é preto e grosso em todos, na mesma espessura, sobre grade de 24 — é o traço de
 * quadrinho, e é o mesmo `2px solid black` que a própria barra já usa na borda dos botões.
 *
 * As cores saem da paleta do guia (`especificacao/marca/HQ-LAB_STYLEGUIDE.pdf`, paleta do Inventor):
 * verde menta, laranja suave, amarelo claro, cinza claro, azul petróleo, grafite.
 */

type Props = { className?: string };

const MENTA = "#7FD1B9";
const AMARELO = "#F7D06A";
const LARANJA = "#F2A65A";
const CINZA = "#D9DEE3";
const TINTA = "#1B1B1B";
const PAPEL = "#FFFBF2";
const PETROLEO = "#1F6F78";

/**
 * A moldura comum. Todo ícone é um tile arredondado com o traço preto por fora — a mesma gramática
 * do quadro de HQ, que é o que este produto desenha.
 *
 * `aria-hidden` porque o rótulo em texto está ao lado, no próprio botão: um leitor de tela que
 * anunciasse o ícone leria tudo duas vezes.
 */
function Tile({
  cor,
  children,
  className,
}: Props & { cor: string; children: React.ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
      focusable="false"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="0.9"
        y="0.9"
        width="22.2"
        height="22.2"
        rx="4.5"
        fill={cor}
        stroke={TINTA}
        strokeWidth="1.8"
      />
      <g
        fill="none"
        stroke={TINTA}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {children}
      </g>
    </svg>
  );
}

/** Personagens — uma cabeça e os ombros. O rosto não tem traço de feição: o guia proíbe reinterpretar os personagens da marca, e um rostinho genérico aqui competiria com eles. */
export function IconePersonagens({ className }: Props) {
  return (
    <Tile cor={MENTA} className={className}>
      <circle cx="12" cy="9" r="3.4" fill={PAPEL} />
      <path d="M5.6 19c0-3.4 2.9-5.6 6.4-5.6s6.4 2.2 6.4 5.6" fill={PAPEL} />
    </Tile>
  );
}

/** Cenários — montanha e sol dentro de uma moldura: o LUGAR onde a cena acontece. */
export function IconeCenarios({ className }: Props) {
  return (
    <Tile cor={MENTA} className={className}>
      <rect x="4.2" y="5.6" width="15.6" height="12.8" rx="1.4" fill={PAPEL} />
      <circle cx="8.6" cy="9.6" r="1.5" />
      <path d="M4.2 15.4l4.2-3.8 3.3 3 2.7-2.4 5.4 4.6" />
    </Tile>
  );
}

/** Textos — o balão de fala, que é o que "texto" quer dizer em quadrinho. */
export function IconeTextos({ className }: Props) {
  return (
    <Tile cor={AMARELO} className={className}>
      <path
        d="M4.8 7.4c0-1 .8-1.8 1.8-1.8h10.8c1 0 1.8.8 1.8 1.8v6.2c0 1-.8 1.8-1.8 1.8h-5.4L8 19v-3.6H6.6c-1 0-1.8-.8-1.8-1.8z"
        fill={PAPEL}
      />
      <path d="M8 9.4h8M8 12.2h5.2" />
    </Tile>
  );
}

/** Figuras — objeto solto que entra na cena. Três sólidos, porque figura é coisa e não gente nem lugar. */
export function IconeFiguras({ className }: Props) {
  return (
    <Tile cor={MENTA} className={className}>
      <circle cx="8.4" cy="8.6" r="3.1" fill={PAPEL} />
      <rect x="12.6" y="5.8" width="5.8" height="5.6" rx="1" fill={PAPEL} />
      <path d="M11.9 18.4l3.2-5.2 3.2 5.2z" fill={PAPEL} />
      <path d="M5.4 14.6h4.6v3.8H5.4z" fill={PAPEL} />
    </Tile>
  );
}

/** Histórico — a seta que volta no tempo, e não um relógio: o que a tela mostra é o que JÁ foi pedido. */
export function IconeHistorico({ className }: Props) {
  return (
    <Tile cor={CINZA} className={className}>
      <path d="M5.4 12a6.6 6.6 0 1 0 2-4.7" fill="none" stroke={PETROLEO} strokeWidth="1.9" />
      <path d="M5.1 5.1v3.6h3.6" stroke={PETROLEO} strokeWidth="1.9" />
      <path d="M12 8.6V12l2.6 1.6" />
    </Tile>
  );
}

/** Inserir Quadro — o retângulo de HQ com o sinal de mais. */
export function IconeInserirQuadro({ className }: Props) {
  return (
    <Tile cor={LARANJA} className={className}>
      <rect x="4.4" y="5.4" width="15.2" height="13.2" rx="1.2" fill={PAPEL} />
      <path d="M12 9v6M9 12h6" />
    </Tile>
  );
}

/** Redimensionar — a folha e a seta diagonal que a estica. */
export function IconeRedimensionar({ className }: Props) {
  return (
    <Tile cor={LARANJA} className={className}>
      <rect x="4.4" y="4.4" width="10.4" height="10.4" rx="1.2" fill={PAPEL} />
      <path d="M11.4 19.6h8.2v-8.2" />
      <path d="M13.6 17.4l6-6" />
    </Tile>
  );
}

/** Exportar para PDF — a folha com a seta para baixo. */
export function IconeExportarPdf({ className }: Props) {
  return (
    <Tile cor={CINZA} className={className}>
      <path d="M6.8 4.6h7l4.4 4.3v10.5H6.8z" fill={PAPEL} />
      <path d="M13.6 4.6v4.4h4.6" />
      <path d="M12.4 11.4v5M10.1 14.1l2.3 2.3 2.3-2.3" stroke={PETROLEO} strokeWidth="1.9" />
    </Tile>
  );
}

/** Gerar imagem — a moldura com a faísca: a máquina que desenha. */
export function IconeGerarImagem({ className }: Props) {
  return (
    <Tile cor={CINZA} className={className}>
      <rect x="4.2" y="5.6" width="12.4" height="12.4" rx="1.4" fill={PAPEL} />
      <path d="M4.2 15l3.6-3.3 2.8 2.6 2.3-2 3.7 3.2" />
      <path
        d="M18.2 3.4l.9 2.3 2.3.9-2.3.9-.9 2.3-.9-2.3-2.3-.9 2.3-.9z"
        fill={AMARELO}
        stroke={TINTA}
        strokeWidth="1.4"
      />
    </Tile>
  );
}

/**
 * Limpar — a lixeira. Fica na barra de baixo, junto com Salvar, e as duas eram PNG também.
 *
 * Vermelho é o único lugar do editor em que ele aparece, e é de propósito: é a única ação da barra
 * que destrói trabalho. Cor de aviso usada em tudo não avisa nada.
 */
export function IconeLimpar({ className }: Props) {
  return (
    <Tile cor="#F0A9A0" className={className}>
      <path d="M5.8 7.2h12.4" />
      <path d="M9.4 7.2V5.4h5.2v1.8" />
      <path d="M7.2 7.2l.9 11.2h7.8l.9-11.2z" fill={PAPEL} />
      <path d="M10.4 10.2v5.2M13.6 10.2v5.2" />
    </Tile>
  );
}

/** Salvar — o visto. É a ação que termina bem, e o verde do guia diz isso sem texto. */
export function IconeSalvar({ className }: Props) {
  return (
    <Tile cor={MENTA} className={className}>
      <circle cx="12" cy="12" r="7.2" fill={PAPEL} />
      <path d="M8.4 12.2l2.6 2.6 4.8-5.2" stroke={PETROLEO} strokeWidth="2" />
    </Tile>
  );
}
